import {
    Component,
    signal,
    inject,
    DestroyRef,
    computed,
    OnInit
} from '@angular/core';
import {SpinnerModule} from '../../shared/spinner/spinner.module';
import {AdsService} from '../../../core/services/cms/ads.service';
import {MatTableModule} from '@angular/material/table';
import {MatIcon, MatIconModule} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AdsLocationsPipe} from '../../../core/pipes/adsLocations.pipe';
import {TableSkeletonComponent} from '../../shared/skeletons/table-skeleton.component';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSortModule, Sort} from '@angular/material/sort';
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {ConfirmationDialogModel} from "../../shared/confirmation-dialog/confirmation-dialog.model";
import {ConfirmationDialogComponent} from "../../shared/confirmation-dialog/confirmation-dialog.component";
import {NotificationModule} from "../../shared/notification/notification.module";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AdsForm2026Component} from "./ads-form-2026/ads-form-2026.component";
import {MarketingService} from "../../../core/services/marketing.service";
import {ReactiveFormsModule} from "@angular/forms";

@Component({
    selector: 'phx-ads-management',
    standalone: true,
    imports: [
        SpinnerModule,
        MatTableModule,
        MatIcon,
        MatTooltip,
        AdsLocationsPipe,
        TableSkeletonComponent,
        MatPaginator,
        MatSortModule,
        MatInputModule,
        MatFormFieldModule,
        MatIconModule,
        MatSelect,
        MatOption,
        NotificationModule,
        ReactiveFormsModule
    ],
    templateUrl: './ads-management-2026.component.html',
    styleUrl: './ads-management-2026.component.scss'
})
export class AdsManagement2026Component implements OnInit {
    protected dialog = inject(MatDialog);
    protected adsService = inject(AdsService);
    protected marketingService = inject(MarketingService);
    protected destroyRef = inject(DestroyRef);

    size = signal(20);
    page = signal(0);
    filter = signal('');
    filterStatus = signal<string>('');
    company = signal('');
    location = signal('');
    status = signal('ALL');

    readonly locations = this.adsService.locations;

    sorting = signal({field: 'startDate', direction: 'desc' as 'asc' | 'desc'});

    displayedColumnsSlides: string[] = ['status', 'image', 'title', 'link', 'date', 'location', 'portal', 'actions'];

    tableData = computed(() => {
        const response = this.adsService.ads();
        const ads: any = response?.content || [];
        const statusFilter = this.filterStatus();


        return ads.map((ad: any) => {
            const now = new Date();
            const start = new Date(ad.start_date);
            const end = new Date(ad.end_date);
            end.setHours(23, 59, 59, 999);

            let status = 'unknown';
            if (now > start && now < end) {
                status = 'active';
            } else if (now < start) {
                status = 'pending';
            } else if (now > end) {
                status = 'finished';
            }

            return {
                id: ad.id || 0,
                location: ad.location || 'Error',
                title: ad.title || '',
                link: ad.link || '',
                imagePath: ad.image || '',
                onHageda: ad.company?.indexOf('HAGEDA') > -1 || false,
                onPhoenix: ad.company?.indexOf('PHOENIX') > -1 || false,
                start: ad.start_date ? new Date(ad.start_date).toLocaleDateString('de-DE') : '',
                end: ad.end_date ? new Date(ad.end_date).toLocaleDateString('de-DE') : '',
                status: status
            };
        }).filter((ad: any) => {
            if (!statusFilter) {
                return true;
            }
            return ad.status === statusFilter;
        });
    });

    totalElements = computed(() => {
        const res = this.adsService.ads();
        return res?.total_elements || res?.totalElements || 0;
    });

    inputFilter = signal('');
    private debounceTimer: any;


    ngOnInit() {
        this.loadAds();
    }

    protected handlePageEvent(event: PageEvent) {
        this.page.set(event.pageIndex);
        this.size.set(event.pageSize);
        this.loadAds();
    }

    protected handleSortChange(sortEvent: Sort) {
        this.page.set(0);
        this.sorting.set({
            field: sortEvent.active,
            direction: sortEvent.direction as 'asc' | 'desc'
        });
        this.loadAds();
    }

    private loadAds() {
        this.adsService.getAds(
            this.page(),
            this.size(),
            this.filter(),
            this.sorting(),
            this.company(),
            this.location(),
            this.status()
        );
    }

    protected showImagePreview(imagePath: string) {
        this.dialog.open(PreviewImageDialog, {
            data: {imagePath}
        });
    }

    protected createAd() {
        const dialogRef = this.dialog.open(AdsForm2026Component, {
            width: '900px',
            data: {mode: 'create'}
        });

        dialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                if (result && result.imageFile) {
                    this.marketingService.uploadImage(result.imageFile, 'ads/' + result.imageFile.name)
                        .then(() => {
                            result.data.image = 'ads/' + result.imageFile.name;
                            this.adsService.saveAd(result.data)
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe({
                                    next: () => {
                                        this.loadAds();
                                        document.getElementById('NotificationContainer')?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start',
                                            inline: 'nearest'
                                        });
                                    },
                                    error: (err) => {
                                        console.error('❌ Error creating ad:', err);
                                    }
                                });
                        })
                        .catch(err => {
                            console.error('❌ Error uploading image:', err);
                        });
                }
            });
    }

    protected editAd(element: any) {
        const originalAd = this.adsService.ads()?.content?.find((ad: any) => ad.id === element.id);

        const dialogRef = this.dialog.open(AdsForm2026Component, {
            width: '900px',
            data: {mode: 'edit', ad: originalAd}
        });

        dialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                if (result) {
                    const saveAndLoad = () => {
                        this.adsService.saveAd(result.data)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe({
                                next: () => {
                                    this.loadAds();
                                    document.getElementById('NotificationContainer')?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                        inline: 'nearest'
                                    });
                                },
                                error: (err) => {
                                    console.error('❌ Error creating ad:', err);
                                }
                            });
                    };

                    if (result.imageFile) {
                        this.marketingService.uploadImage(result.imageFile, 'ads/' + result.imageFile.name)
                            .then(() => {
                                result.data.image = 'ads/' + result.imageFile.name;
                                saveAndLoad();
                            });
                    } else {
                        saveAndLoad();
                    }
                }
            });
    }

    protected deleteAd(element: any) {
        const adId = element['id'];
        const dialogData = new ConfirmationDialogModel('Ad entfernen', 'Möchten Sie Ad wirklich löschen?');
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '400px',
            closeOnNavigation: true,
            data: dialogData
        });

        dialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(dialogResult => {
                if (dialogResult) {
                    this.adsService.deleteAd(adId)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: () => {
                                this.loadAds();
                                document.getElementById('NotificationContainer')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                    inline: 'nearest'
                                });
                            }
                        });
                }
            });
    }


    protected onInputChange(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.inputFilter.set(value);
    }

    protected triggerSearch() {
        this.filter.set(this.inputFilter());
        this.page.set(0);
        this.loadAds();
    }

    protected clearFilter() {
        this.inputFilter.set('');
        this.filter.set('');
        this.page.set(0);
        this.loadAds();
    }

    protected onStatusChange(value: string) {
        this.status.set(value);
        this.page.set(0);
        this.loadAds();
    }

    protected onCompanyChange(value: any) {
        this.company.set(value);
        this.page.set(0);
        this.loadAds();
    }

    protected onLocationChange(value: any) {
        this.location.set(value);
        this.page.set(0);
        this.loadAds();
    }

    protected resetFilter() {
        this.inputFilter.set('');
        this.filter.set('');
        this.status.set('ALL');
        this.company.set('');
        this.location.set('');
        this.page.set(0);
        this.loadAds();
    }
}

@Component({
    selector: 'preview-image-dialog',
    templateUrl: 'preview-image-dialog.html',
    standalone: true
})
export class PreviewImageDialog {
    dialogRef = inject(MatDialogRef<PreviewImageDialog>);
    data = inject(MAT_DIALOG_DATA);
    protected CDN = inject('CDN' as any) as string;

    close(): void {
        this.dialogRef.close();
    }
}
